import styles from './AddressInput.module.scss';
import * as React from 'react';
import Geosuggest, { Suggest } from 'react-geosuggest';

import { useState, useEffect, useRef } from 'react';
import { CloseIcon } from './CloseIcon';
import { getFullAddressString } from '../../util/Address';

export interface SuggestAddress {
  /**
   * @deprecated Passing the full string address is deprecated as of August 2022.
   * It should be kept in the typedef for interop until it is removed from the API response.
   */
  address?: string;
  streetAddress: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  establishment?: string
  googlePlaceId: string;
}

type GeocoderResultOrPlaceDetail = google.maps.GeocoderResult & Partial<Pick<google.maps.places.PlaceResult, "name" | "adr_address">>;

const COMPONENT_TYPES = {
  city: ['locality', 'administrative_area_level_3', 'sublocality_level_1'],
  state: ['administrative_area_level_1'],
  country: ['country'],
  zipCode: ['postal_code'],
  postal_code: ['postal_code'],
};

export const AddressInput = ({ initialValue, onSelect, onTextChange }: { initialValue?: string; onSelect: (selectedSuggest?: SuggestAddress) => void, onTextChange?: (value: string) => void }) => {
  const [suggest, setSuggest] = useState<SuggestAddress>();
  const suggestRef = useRef<Geosuggest | null>(null);

  const parseGooglePlace = (place: GeocoderResultOrPlaceDetail) => {
    const { formatted_address: address, geometry } = place;
    // Note: This doesn't catch omitted fields because of the type coercion, however it's
    // necessary to support the additive construction logic below.
    const newSuggest = { latitude: geometry.location.lat(), longitude: geometry.location.lng(), googlePlaceId: place.place_id } as SuggestAddress;

    // Google's API doesn't provide a pure string representation of the street address.
    // Use the ADR microformat string it does give to identify the segment by class name.
    // This strategy omits the "extended address" (AKA place name).

    // This parsing function is actually called with two different types depending on context; one is a superset of the other.
    // In cases where we're not fetching place details (see "reverse geocode"), we may not have the HTML string.
    // In such cases, we fall back to grabbing everything before the first comma.

    // Overall, this saves us from a lot of grief manually assembling the street address from components.
    // By way of example, `subpremise` (AKA apartment number) always appears first in `address_components`,
    // and has any '#'s included with the request stripped. However, it doesn't strip string prefixes, e.g.,
    // "Apt." or "Unit".

    const adrString = place.adr_address;

    if (adrString) {
      // Replace the code below with a RegEx match as shown once lookbehind assertions are supported in Safari.
      // newSuggest.streetAddress = htmlString.match(/(?<=street-address">)[^<]*/)?.[0] ?? '';
      const searchTerm = "street-address\">";
      const tagLocation = adrString.indexOf(searchTerm);
      if (tagLocation > -1) {
        newSuggest.streetAddress = adrString.substring(tagLocation + searchTerm.length, adrString.indexOf("<", tagLocation));
      }

      // If the place name we were given has a name that doesn't match the street address, it is likely an establishment name.
      // For non-establishment results (e.g., a home address), `name` generally mirrors the street address.
      if (place.name !== newSuggest.streetAddress) {
        newSuggest.establishment = place.name;
      }
    }

    // If we can't find the street address using the method above, fall back to grabbing the full address up to the first comma.
    // This may get the wrong segment if the street address itself contains a comma, or a business name is included in the address.
    // For this reason, the comma method is a last resort. Ultimately, if this messes up, the user can just retype their address.
    newSuggest.streetAddress ??= address.match(/^[^,]+/)?.[0] ?? '';

    place.address_components.forEach((component: google.maps.GeocoderAddressComponent) => {
      const value = component.long_name;

      if (component.types.some((type) => COMPONENT_TYPES.city.indexOf(type) > -1)) {
        newSuggest.city = value;
      } else if (component.types.some((type) => COMPONENT_TYPES.state.indexOf(type) > -1)) {
        newSuggest.state = component.short_name; // 2 letter name
      } else if (component.types.some((type) => COMPONENT_TYPES.country.indexOf(type) > -1)) {
        newSuggest.country = component.short_name; // e.g., US instead of United States
      } else if (component.types.some((type) => COMPONENT_TYPES.postal_code.indexOf(type) > -1)) {
        newSuggest.zipcode = value;
      }
    });

    setSuggest(newSuggest);
    // Force the input value to reflect the new suggestion text. This is necessary because if the
    // user selects a suggestion that is autocorrected, then retypes their search and clicks that
    // same suggestion again, we won't receive a change event since the active suggestion hasn't changed.
    suggestRef.current?.update(getFullAddressString(newSuggest));
  };

  const onSuggestSelect = (selectedSuggest?: Suggest) => {
    if (!selectedSuggest) {
      return;
    }

    const suggestHasStreetNumber = selectedSuggest.gmaps?.address_components.some((component: google.maps.GeocoderAddressComponent) =>
      component.types.includes('street_number')
    );

    if (suggestHasStreetNumber && !!selectedSuggest.gmaps) {
      parseGooglePlace(selectedSuggest.gmaps);
      return;
    }

    // User is attempting to save an address with no street number. Reverse geocode a more precise address from suggest location.
    const geocode = new google.maps.Geocoder();
    geocode.geocode({ location: selectedSuggest.location }, (result, status) => {
      if (!result || status !== 'OK') return;

      for (const geocoderResult of result) {
        // Find the first result that doesn't have a Plus Code and does have a street number.
        if (geocoderResult.address_components[0].types.includes("plus_code") || !geocoderResult.address_components.some(v => v.types.includes("street_number"))) {
          continue;
        }

        parseGooglePlace(geocoderResult);
        return;
      }

      // If we reach the end of the suggestion list without finding a non-Plus Code address with a street number,
      // we can't do anything useful with the input provided, so blank the field. This should be exceedingly rare
      // in practice, and typically only happens with unroutable locations like "The Wave Trail, UT".
      suggestRef.current?.update('');

      // onError('There was a problem finding this address. Please try again.');
    });
  };

  useEffect(() => {
    if (suggest) {
      onSelect(suggest);
    }
    // Intentional to not have onSelect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggest]);

  const onClearClick = () => {
    if (suggestRef.current) {
      suggestRef.current.clear();
      setTimeout(
        () => {
          if (suggestRef.current) {
            suggestRef.current.focus();
          }
        },
        // geosuggest hides suggest dropdown on blur for 100 ms, so we refocus after that
        120
      );
      onSelect(undefined);
    }
  };

  return (
    <div className={styles.container} data-tid="inp_address">
      <Geosuggest country="US" initialValue={initialValue} autoActivateFirstSuggest={true} ref={suggestRef} placeholder={'Search places'} onSuggestSelect={onSuggestSelect} onChange={onTextChange}/>
      <div className={styles.clearButton} onClick={onClearClick} data-tid="btn_clearField">
        <CloseIcon />
      </div>
    </div>
  );
};
